import Routes from '@services/Routes';

import { ASSETS_URL } from './constants';

/**
 * Closes the notifications dropdown if it is open.
 */
const closeNotificationsDropdown = () => {
  const notificationsButton = document.querySelector('[data-testid="notifications-dropdown-button"]');
  if (notificationsButton) {
    notificationsButton.click();
  }
};

/**
 * Waits for the UnsavedChangesDialog confirm button to appear in the DOM and then listens for a click event on it.
 * @param target
 */
const waitForUnsavedChangesDialogConfirmClick = target => {
  const observer = new MutationObserver((mutations, obs) => {
    const okButton = document.querySelector('[data-testid="unsaved-changes-dialog-ok-button"]');

    if (okButton) {
      const handleOkClick = () => {
        window.location.assign(target);
        okButton.removeEventListener('click', handleOkClick);
      };

      okButton.addEventListener('click', handleOkClick);
      obs.disconnect(); // Stop observing once we find the button
    }
  });

  // Start observing changes to the entire document
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};

/**
 * @param target {String} Target location URL
 * @param router {Object} React router object from calling component
 * @param forceReload {Boolean} Sets whether or not to force a reload of the page.
 */
const handleNav = (target, router, forceReload = false) => {
  const getRequest = url => url.match(/bid_requests\/\d+/)?.[0];
  const currentRequest = getRequest(window.location.pathname);
  const targetRequest = getRequest(target);

  const needsReload =
    forceReload ||
    (currentRequest && targetRequest) && currentRequest !== targetRequest ||
    target.includes('format') ||
    target.includes('schedule');

  // Only check for save changes button if we're on the line items page
  const isOnLineItemsPage = /\/bid_requests\/\d+\/line_items/.test(window.location.pathname);
  const saveChangesButton = isOnLineItemsPage
    ? document.querySelector('[data-testid="line-items-footer-save-changes-button"]')
    : null;

  // If a user has made changes on the line items page, we want to ensure that the UnsavedChangesDialog shows
  // Using window.location.assign will not trigger the modal to show
  // The UnsavedChangesDialog is triggered by the router, so we need to use router.push
  if (saveChangesButton) {
    // If the save changes button is disabled, it means there are no unsaved changes
    if (saveChangesButton.disabled && needsReload) {
      window.location.assign(target);
    } else {
      // If the save changes button is enabled, it means there are unsaved changes
      router.push(target).catch(error => {
        if (error === 'routeChangeAbort') {
          // If the route change was aborted when clicking a redirect from a notification
          // We want to ensure that the notifications dropdown is closed
          // To allow the UnsavedChangesDialog to show without the notifications dropdown overlapping it
          closeNotificationsDropdown();

          // Add listener to UnsavedChangesDialog confirm button to reload the page when user confirms they want to leave the page
          // We need to reload the page instead of using the normal router.push which will not reload the page
          if (needsReload) {
            // Use MutationObserver to wait for the modal to appear in the DOM
            // There is a slight delay before the modal is added to the DOM after the route change is aborted
            waitForUnsavedChangesDialogConfirmClick(target);
          }
        }
      });
    }
  } else if (needsReload) {
    window.location.assign(target);
  } else {
    router.push(target);
  }
};

const viewRequestFormat = ({ bid_request_id: id }, router) => {
  const navUrl = `/bid_requests/${id}/schedule`;

  handleNav(navUrl, router);
};

const viewRequestDashboard = ({ bid_request_id: id }, router) => {
  const navUrl = `/bid_requests/${id}/dashboard`;

  handleNav(navUrl, router);
};

const viewRequestVendorsTab = ({ bid_request_id: id }, router) => {
  const navUrl = `/bid_requests/${id}/suppliers`;

  handleNav(navUrl, router);
};

const viewRequestDocumentsTab = ({ bid_request_id: id }, router) => {
  const navUrl = `/bid_requests/${id}/documents`;

  handleNav(navUrl, router);
};

const viewVendorSurvey = ({ bid_request_id: id }, router) => {
  const navUrl = Routes.REQUEST_SURVEY_PATH(id);

  handleNav(navUrl, router);
};

const viewSuggestedPrices = ({ bid_request_id: id }, router, flags = {}) => {
  const navUrl = flags.newSupplierPreview
    ? `/vendor_bids/${id}/new`
    : Routes.REQUEST_EVENT_LINE_ITEMS_PATH(id, 'preview_supplier_pricing');

  handleNav(navUrl, router);
};

const viewAwardTab = ({ bid_request_id: id }, router) => {
  const navUrl = `/bid_requests/${id}/award`;

  handleNav(navUrl, router);
};

const viewSupplierSurveyAsSupplier = ({ bid_request_id: id }, router) => {
  const navUrl = `/bid_requests/${id}/quote_submission`;

  handleNav(navUrl, router, true);
};

const viewTeamManagementWithEmail = ({ user_email: email }, router) => {
  const navUrl = `/team?search=${email}`;

  handleNav(navUrl, router);
};

const viewVendorsTabWithEmail = ({ user_email: email }, router) => {
  const navUrl = `/suppliers?search=${email}`;

  handleNav(navUrl, router);
};

// These variables are defined on the additional_data from the Ruby template
const viewQuoteSubmission = ({ bid_request_id: id }, router) => {
  const navUrl = Routes.QUOTE_SUBMISSION_PATH(id);

  handleNav(navUrl, router);
};

export const markRead = async (feed, activityId) => {
  await feed.get({ mark_read: [activityId] });
};

/**
 * Marks a group of activities as 'seen', this will decrement the count of unseen
 * @param feed {StreamFeed} feed for this user
 * @param activityIds {Array.<String>} Array of activity ids
 * @returns {Number}
 */
export const markSeen = async (feed, activityIds) => {
  if (activityIds.length > 0) {
    await feed.get({ mark_seen: activityIds });
  }
  return activityIds.length;
};

export const markAllSeen = async feed => {
  await feed.get({ mark_seen: true });
};

export const markAllRead = async feed => {
  await feed.get({ mark_read: true });
};

/**
 * Map for mapping an incoming notification to an appropriate action function
 * @type {Object.<String, Function>}
 */
export const notificationActionsMap = {
  'markRead': markRead,
  'markSeen': markSeen,
  'viewAwardTab': viewAwardTab,
  'viewQuoteSubmission': viewQuoteSubmission,
  'viewRequestDashboard': viewRequestDashboard,
  'viewRequestDocumentsTab': viewRequestDocumentsTab,
  'viewRequestFormat': viewRequestFormat,
  'viewRequestVendorsTab': viewRequestVendorsTab,
  'viewSuggestedPrices': viewSuggestedPrices,
  'viewSupplierSurveyAsSupplier': viewSupplierSurveyAsSupplier,
  'viewTeamManagementWithEmail': viewTeamManagementWithEmail,
  'viewVendorSurvey': viewVendorSurvey,
  'viewVendorsTabWithEmail': viewVendorsTabWithEmail
};

/**
 * Maps incoming notification action to images
 * @type {Object.<String, *>}
 */
export const notificationImageMap = {
  'viewAwardTab':
      <img
        alt='Survey'
        src={`${ASSETS_URL}/images/crown.svg`}
        style={{ height: 40, width: 40 }}
      />,
  'viewRequestDocumentsTab':
      <img
        alt='Document Received'
        src={`${ASSETS_URL}/images/document-received.svg`}
        style={{ height: 40, width: 40 }}
      />,
  'viewRequestFormat': <img alt='Time' src={`${ASSETS_URL}/images/time.svg`} style={{ height: 40, width: 40 }} />,
  'viewRequestVendorsTab':
      <img
        alt='Warning'
        src={`${ASSETS_URL}/images/error-warning.svg`}
        style={{ height: 40, width: 40 }}
      />,
  'viewSuggestedPrices':
      <img
        alt='Warning'
        src={`${ASSETS_URL}/images/error-warning.svg`}
        style={{ height: 40, width: 40 }}
      />,
  'viewSupplierSurveyAsSupplier':
      <img
        alt='Survey'
        src={`${ASSETS_URL}/images/survey.svg`}
        style={{ height: 40, width: 40 }}
      />,
  'viewTeamManagementWithEmail':
      <img
        alt='Warning'
        src={`${ASSETS_URL}/images/message-error.svg`}
        style={{ height: 40, width: 40 }}
      />,
  'viewVendorSurvey':
      <img
        alt='Warning'
        src={`${ASSETS_URL}/images/error-warning.svg`}
        style={{ height: 40, width: 40 }}
      />,
  'viewVendorsTabWithEmail':
      <img
        alt='Warning'
        src={`${ASSETS_URL}/images/message-error.svg`}
        style={{ height: 40, width: 40 }}
      />,
};
